"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  PopoverClose,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useDataStore from "@/stores/data-store";
import { IPathPermission } from "@/types/base-type";
import clsx from "clsx";
import {
  Banknote,
  ClipboardCheck,
  Database,
  FileText,
  NotepadText,
  Package,
  PieChart,
  ShieldCheck,
  User,
  Wrench,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Fragment, useEffect, useState } from "react";
import {
  getApprovalLinks,
  getDataLinks,
  getHrOrganizationLinks,
  getHrPayrollLinks,
  getHrReportLinks,
  getHrToolLinks,
  getMasterDataPolicyLinks,
  getMasterDataPrivilegeLinks,
  getPieceworkDataLinks,
  getTicketCreationLinks,
} from "./sidebar-links";

type SidebarLinkItem = {
  name: string;
  href: string;
};

type SidebarGroup = {
  name: string;
  icon: typeof User;
  links: SidebarLinkItem[];
};

type SidebarSection = {
  title: string;
  items: SidebarGroup[];
};

export default function Sidebar({
  permissions,
}: {
  permissions: IPathPermission[];
}) {
  const isDevelopment = process.env.NODE_ENV === "development";
  const { isSidebarCollapsed, isSidebarOpen, closeSidebar } = useDataStore();
  const pathname = usePathname();
  const t = useTranslations("sidebar");
  const locale = useLocale();
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const sidebarItems: SidebarSection[] = [
    {
      title: t("data.title"),
      items: [
        {
          name: t("data.data.title"),
          icon: Database,
          links: getDataLinks(t, locale),
        },
        {
          name: t("data.piecework.title"),
          icon: Package,
          links: getPieceworkDataLinks(t, locale),
        },
      ],
    },
    {
      title: t("hr.title"),
      items: [
        {
          name: t("hr.organization.title"),
          icon: User,
          links: getHrOrganizationLinks(t, locale),
        },
        {
          name: t("hr.payroll.title"),
          icon: Banknote,
          links: getHrPayrollLinks(t, locale),
        },
        {
          name: t("hr.reports.title"),
          icon: PieChart,
          links: getHrReportLinks(t, locale),
        },
      ],
    },
    {
      title: t("tools.title"),
      items: [
        {
          name: t("tools.payroll"),
          icon: Wrench,
          links: getHrToolLinks(t, locale),
        },
      ],
    },
    {
      title: t("approvals.title"),
      items: [
        {
          name: t("approvals.pending"),
          icon: ClipboardCheck,
          links: getApprovalLinks(t, locale),
        },
        {
          name: t("approvals.ticket-types"),
          icon: FileText,
          links: getTicketCreationLinks(t, locale),
        },
      ],
    },
    {
      title: t("master-data.title"),
      items: [
        {
          name: t("master-data.policies.title"),
          icon: NotepadText,
          links: getMasterDataPolicyLinks(t, locale),
        },
        {
          name: t("master-data.privileges.title"),
          icon: ShieldCheck,
          links: getMasterDataPrivilegeLinks(t, locale),
        },
      ],
    },
  ];

  const visibleSections = sidebarItems
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => ({
          ...item,
          links: isDevelopment
            ? item.links
            : item.links.filter((link) => {
                const hasReadPermission = permissions.some(
                  (permission) =>
                    link.href.startsWith(`/${locale}${permission.path}`) &&
                    permission.permissions.length > 0
                );

                return hasReadPermission;
              }),
        }))
        .filter((item) => item.links.length > 0),
    }))
    .filter((section) => section.items.length > 0);

  useEffect(() => {
    setOpenGroup(null);
  }, [pathname, isSidebarCollapsed]);

  return (
    <Fragment>
      <div
        className={clsx("lg:hidden", {
          "fixed inset-x-0 bottom-0 top-[58px] z-10 bg-black/30 backdrop-blur-[1px]":
            isSidebarOpen,
        })}
        onClick={closeSidebar}
      />

      <aside
        className={clsx(
          "fixed top-[58px] z-50 h-[calc(100svh-58px)] shrink-0 overflow-y-auto overscroll-contain border-r border-white/10 surface-sidebar py-3 text-sm font-medium shadow-overlay lg:translate-x-0",
          hasMounted && "transition-[width,transform] duration-300 ease-out",
          isSidebarCollapsed ? "w-64 lg:w-[76px]" : "w-64",
          {
            "-translate-x-full": !isSidebarOpen,
          }
        )}
      >
        {isSidebarCollapsed && (
          <div className="hidden space-y-3 px-2 lg:block">
            {visibleSections.map((section) => (
              <div key={section.title} className="space-y-1.5 pb-0.5">
                <div className="flex justify-center py-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-300/50" />
                </div>

                <div className="border-white/8 space-y-1.5 rounded-[20px] border bg-white/[0.045] px-1.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                  {section.items.map((item) => {
                    const isGroupActive = item.links.some((link) =>
                      pathname.startsWith(link.href)
                    );
                    const Icon = item.icon;
                    const groupKey = `${section.title}-${item.name}`;

                    return (
                      <Popover
                        key={groupKey}
                        open={openGroup === groupKey}
                        onOpenChange={(open) => {
                          setOpenGroup(open ? groupKey : null);
                        }}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className={clsx(
                              "flex h-9 w-full items-center justify-center rounded-xl border transition-colors",
                              isGroupActive || openGroup === groupKey
                                ? "bg-sky-400/12 border-sky-400/25 text-white ring-1 ring-sky-300/10"
                                : "border-transparent text-white/70 hover:bg-white/[0.055] hover:text-white"
                            )}
                            aria-label={item.name}
                          >
                            <Icon className="size-4" />
                          </button>
                        </PopoverTrigger>

                        <PopoverContent
                          side="right"
                          align="start"
                          sideOffset={14}
                          collisionPadding={{ top: 70, bottom: 16, right: 16 }}
                          className="bg-white/96 max-h-[calc(100svh-86px)] w-64 overflow-y-auto overscroll-contain rounded-2xl border border-slate-200/85 p-2 text-slate-900 shadow-overlay backdrop-blur dark:border-slate-700/85 dark:bg-slate-800/96 dark:text-slate-100"
                        >
                          <div className="sticky top-0 z-[1] mb-2 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-700/60">
                            <span className="bg-[var(--primary-alt)]/12 flex size-8 items-center justify-center rounded-lg text-[var(--primary-alt)]">
                              <Icon className="size-4" />
                            </span>
                            <div>
                              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                                {section.title}
                              </div>
                              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {item.name}
                              </div>
                            </div>
                          </div>

                          <ul className="space-y-1">
                            {item.links.map((link) => {
                              const isActive = pathname === link.href;

                              return (
                                <li key={link.href}>
                                  <PopoverClose asChild>
                                    <Link
                                      href={link.href}
                                      onClick={() => {
                                        setOpenGroup(null);
                                        closeSidebar();
                                      }}
                                      className={clsx(
                                        "flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] transition-colors",
                                        isActive
                                          ? "bg-[var(--primary-alt)]/10 font-medium text-[var(--primary-alt)]"
                                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700/60 dark:hover:text-slate-100"
                                      )}
                                    >
                                      <span
                                        className={clsx(
                                          "size-1.5 rounded-full",
                                          isActive
                                            ? "bg-[var(--primary-alt)]"
                                            : "bg-slate-300 dark:bg-slate-600"
                                        )}
                                      />
                                      <span>{link.name}</span>
                                    </Link>
                                  </PopoverClose>
                                </li>
                              );
                            })}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className={clsx(isSidebarCollapsed ? "lg:hidden" : "block")}>
          <Accordion
            type="single"
            collapsible
            className="w-full px-3 capitalize text-white/75"
          >
            {visibleSections.map((section, sectionIndex) => (
              <div key={section.title} className="pb-1">
                <div className="relative mt-4 select-none pb-2 pl-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/40 first:mt-0">
                  <span className="absolute left-0 top-[9px] h-1 w-1 rounded-full bg-sky-300/55" />
                  {section.title}
                </div>

                {section.items.map((item, itemIndex) => {
                  const isGroupActive = item.links.some((l) =>
                    pathname.startsWith(l.href)
                  );

                  return (
                    <AccordionItem
                      key={item.name}
                      value={`item-${sectionIndex}-${itemIndex}`}
                      className="border-transparent"
                    >
                      <AccordionTrigger className="rounded-xl px-3 py-2 text-[13px] hover:bg-white/[0.045] hover:no-underline">
                        <div
                          className={clsx(
                            "flex items-center gap-3 transition-colors",
                            isGroupActive
                              ? "text-white"
                              : "text-white/72 hover:text-white"
                          )}
                        >
                          <span
                            className={clsx(
                              "flex size-7 items-center justify-center rounded-lg border transition-colors",
                              isGroupActive
                                ? "border-sky-400/25 bg-sky-400/10 text-sky-100"
                                : "border-white/10 bg-white/[0.045] text-white/75"
                            )}
                          >
                            <item.icon className="size-4" />
                          </span>
                          <span>{item.name}</span>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="pb-1">
                        <ul className="border-white/8 mt-1 space-y-1 rounded-2xl border bg-white/[0.045] p-2 font-normal shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                          {item.links.map((link) => {
                            const isActive = pathname === link.href;

                            return (
                              <li key={link.href}>
                                <Link
                                  href={link.href}
                                  onClick={closeSidebar}
                                  className={clsx(
                                    "relative block select-none rounded-xl py-[7px] pl-7 pr-3 text-[13px] transition-colors",
                                    isActive
                                      ? [
                                          "ring-sky-300/12 bg-sky-400/10 font-medium text-white ring-1",
                                          "before:absolute before:left-2 before:top-1/2 before:size-1.5 before:-translate-y-1/2 before:rounded-full before:bg-sky-300",
                                        ]
                                      : [
                                          "text-white/68",
                                          "before:bg-white/22 before:absolute before:left-2 before:top-1/2 before:size-1.5 before:-translate-y-1/2 before:rounded-full",
                                          "hover:bg-white/[0.055] hover:text-white",
                                        ]
                                  )}
                                >
                                  {link.name}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </div>
            ))}
          </Accordion>
        </div>
      </aside>
    </Fragment>
  );
}
